/**
 * The signed-in account's invoice address.
 *
 * Reads and writes go through two SECURITY DEFINER functions (see
 * 20260814120000_owner_settings_billing_address.sql) rather than a table,
 * because the row that matters is `lessors` — the record management invoices
 * are addressed from — and that table is admin/service-role only. The function
 * resolves the caller's lessor with the same resolver invoice attribution uses,
 * so what an owner types here is what their next invoice prints.
 *
 * `source` tells the UI which row answered:
 *   'lessor'    — editable, and it feeds invoices
 *   'ambiguous' — read-only; the account maps to several lessors and picking
 *                 one would silently re-address someone's invoices
 *   'none'      — read-only; no owner record exists yet, so there is nothing
 *                 to bill and nowhere to save. Deliberately NOT a profiles
 *                 fallback: that second copy was dropped in
 *                 20260813120000_remove_settings_billing_address.sql because
 *                 nothing ever billed from it.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { invalidateOwnerCaches } from "@/hooks/v2/owner-detail-keys";

export type BillingAddressSource = "lessor" | "ambiguous" | "none";

export type AccountBillingAddress = {
  source: BillingAddressSource;
  editable: boolean;
  lessor_id: string | null;
  lessor_name: string | null;
  address: string | null;
  city_postal: string | null;
};

/** Defaults for a partial payload: assume NOT editable until told otherwise. */
const EMPTY: AccountBillingAddress = {
  source: "none",
  editable: false,
  lessor_id: null,
  lessor_name: null,
  address: null,
  city_postal: null,
};

export function accountBillingAddressKey(userId: string | undefined) {
  return ["account-billing-address", userId] as const;
}

/**
 * True when the failure is "this database has no such function" — i.e. the
 * migration has not been applied to the project the client is pointed at.
 * Environments here drift (the dev project trails LIVE), and a Settings tab
 * must not break because of it: the caller degrades to hiding the field
 * instead of surfacing an error the user can do nothing about.
 */
function isMissingFunction(error: {
  code?: string;
  message?: string;
}): boolean {
  if (error?.code === "PGRST202" || error?.code === "42883") return true;
  return /could not find the function|does not exist/i.test(
    error?.message ?? ""
  );
}

export function useAccountBillingAddress(enabled = true) {
  const { user } = useAuth();

  return useQuery<AccountBillingAddress | null>({
    queryKey: accountBillingAddressKey(user?.id),
    enabled: enabled && !!user?.id,
    retry: false,
    queryFn: async () => {
      const { data, error } = await (
        supabase as unknown as {
          rpc: (fn: string) => Promise<{
            data: unknown;
            error: { code?: string; message?: string } | null;
          }>;
        }
      ).rpc("account_billing_address");
      if (error) {
        if (isMissingFunction(error)) return null;
        throw error;
      }
      return { ...EMPTY, ...((data ?? {}) as Partial<AccountBillingAddress>) };
    },
  });
}

export function useSaveAccountBillingAddress() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (input: { address: string; cityPostal: string }) => {
      const { data, error } = await (
        supabase as unknown as {
          rpc: (
            fn: string,
            args: Record<string, string>
          ) => Promise<{ data: unknown; error: Error | null }>;
        }
      ).rpc("save_account_billing_address", {
        _address: input.address,
        _city_postal: input.cityPostal,
      });
      if (error) throw error;
      return { ...EMPTY, ...((data ?? {}) as Partial<AccountBillingAddress>) };
    },
    onSuccess: (saved) => {
      queryClient.setQueryData(accountBillingAddressKey(user?.id), saved);
      // Every admin owner surface reads the same lessor row this RPC just
      // wrote — the Owners list, the owner profile and its portfolio, not only
      // the raw `lessors` cache this used to name.
      invalidateOwnerCaches(queryClient, saved.lessor_id ?? null);
    },
  });
}
